/**
 * S0b 카운터 (DEV_SPEC 4.1 1막·2막·마무리, 프로토타입 isCounter).
 *
 *   차 칩 6~7개 → 이모의 차 한 줄 → 이름부터 대는 호칭 묻기 → 기분 · 꼬리 질문 → 하는 일 소개 + 이모 명함
 *   → 장부 문장 + 장부 카드(적어 주세요 → 이모 대꾸 → 하단 「장부 · 생일」 시트) → 두 줄 풀이 → 별채 기척(문틈 사진)
 *   → 별채 초대(한 줄 하고 · 인사만 · 이모랑만) → 별채에 올릴 말 → (고민이면 한 번 묻기) → 단골 카드 → 2막 장면 전환
 *   (시안 09 · 08 K1~K6 · 14 L1~L7, D53)
 *
 * - 장면, 칩, 입력창은 서버가 정합니다. 이 화면은 받은 대로 그리고 고른 것을 보냅니다
 * - 장부 카드는 펼치지 않습니다(14 결정 60). 「적어 주세요」 → 이모 대꾸가 보이고 400ms 뒤 생일 시트(LedgerBirthSheet).
 *   생일 단계엔 칩 줄이 없고, 시트를 닫으면 「다음에요」(결정 62). 적은 생일은 내 말풍선 문장으로(결정 63)
 * - 「한 줄 하고 들어갈게요」면 초안을 받아 「별채에 올릴 말」 카드에 넣습니다. 유저가 고친 문장 그대로,
 *   「이대로 올리기」를 눌러야 올라갑니다. 이모와 나눈 대화는 올라가지 않습니다
 * - 위기(L2 이상)면 대본이 멈추고 이모 위기 말풍선 + 109 · 119 버튼(안전 카드 없음, C-020). 그날은 카운터에서 이모와 이어 얘기,
 *   다음 날 다시 오면 멈춘 단계부터(서버)
 * - 단골 카드에서만 알림 권한을 묻습니다
 * - 나가기(핸드오프 15, 결정 65~68): 머리 ‹ · 안드로이드 뒤로 · 브라우저 뒤로(iOS 가장자리 스와이프) = 같은 동작.
 *   열린 시트부터 닫고(생일 시트 닫기 = 다음에요), 차 고르기 전이면 바로 골목(/welcome?paused=1), 그 뒤면 이모가 한 번 붙잡기(ExitSheet).
 *   대화에는 흔적을 남기지 않습니다. 다시 오면 서버가 멈춘 단계부터 인사 + 질문을 붙여 보냅니다
 */
import { ChevronLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MEMBERS } from '@config/members/index.js';
import { SUMMARY_MAX_LENGTH } from '@config/onboarding/counterScript.js';
import { Avatar } from '../../components/Avatar.jsx';
import { Button } from '../../components/Button.jsx';
import { MemberMessage, MyMessage } from '../../components/MessageBubbles.jsx';
import { SceneLine } from '../../components/SceneLine.jsx';
import { TypingIndicator } from '../../components/TypingIndicator.jsx';
import { BottomSheet } from '../../components/BottomSheet.jsx';
import { InputBox } from '../../components/InputBox.jsx';
import { errorMessageOf } from '../../api/client.js';
import * as counterApi from '../../api/counter.js';
import { useSession } from '../../stores/session.js';
import { useCounter } from './useCounter.js';
import { RegularCard } from './RegularCard.jsx';
import { LedgerCard } from './LedgerCard.jsx';
import { LedgerBirthSheet } from './LedgerBirthSheet.jsx';
import { EXIT_LABEL, ExitSheet } from './ExitSheet.jsx';
import { track } from '../../api/track.js';
import { prefersReducedMotion } from '../../stores/revealSpeed.js';

const IMO = MEMBERS.imo;
// 이모 대꾸(ledgerAccept)가 보인 뒤 시트까지(14 L3)
const BIRTH_SHEET_DELAY_MS = 400;
// 나가기 → 골목: 카운터 화면이 사라지는 시간(15 4장)
const LEAVE_FADE_MS = 240;
export const PAUSED_PATH = '/welcome?paused=1';

/** 「저녁 · 처음 온 날」 */
export function partOfDay(date = new Date()) {
    const hour = date.getHours();
    if (hour < 5) {
        return '밤';
    }
    if (hour < 11) {
        return '아침';
    }
    if (hour < 17) {
        return '낮';
    }
    if (hour < 21) {
        return '저녁';
    }
    return '밤';
}

export function CounterPage({ lineDelays }) {
    const navigate = useNavigate();
    const setUser = useSession((state) => state.setUser);
    const counter = useCounter({
        delays: lineDelays,
        onFinished(response) {
            if (response.user) {
                setUser(response.user);
            }
            navigate(response.redirect ?? '/', { replace: true });
        },
    });
    const { view, messages } = counter;
    const scrollRef = useRef(null);

    useEffect(() => {
        const element = scrollRef.current;
        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    }, [messages.length, counter.imoTyping, view?.step]);

    useEffect(() => {
        if (counter.view?.user) {
            setUser(counter.view.user);
        }
    }, [counter.view, setUser]);

    const step = view?.step;
    const showControls = counter.status === 'ready' && !counter.imoTyping;
    const [imoSheetOpen, setImoSheetOpen] = useState(false);
    // 생일 시트: 이모 대꾸가 다 보인 뒤에만. 보내다 실패하면 다시 띄우지 않고 「다시 보내기」로
    const [birthSheetOpen, setBirthSheetOpen] = useState(false);
    const birthReady = showControls && step === 'birth' && !counter.canRetry;
    useEffect(() => {
        if (!birthReady) {
            setBirthSheetOpen(false);
            return undefined;
        }
        const timer = setTimeout(() => setBirthSheetOpen(true), BIRTH_SHEET_DELAY_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [birthReady]);

    // ── 나가기(핸드오프 15) ──
    const [exitOpen, setExitOpen] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const saidAnything = messages.some((message) => message.role === 'user');

    function leave() {
        setExitOpen(false);
        setLeaving(true);
        const go = () =>
            navigate(PAUSED_PATH, {
                replace: true,
                state: { speech: view?.speech ?? 'casual', line: view?.lines?.exitAlley ?? null },
            });
        if (prefersReducedMotion()) {
            go();
        } else {
            setTimeout(go, LEAVE_FADE_MS);
        }
    }

    /** ‹ · 뒤로 한 번: 열린 시트부터 닫음(한 번에 하나, 결정 67) → 차 전이면 바로 골목 → 아니면 붙잡기 */
    function requestExit() {
        if (leaving) {
            return;
        }
        if (exitOpen) {
            setExitOpen(false);
            track('counter_exit_hold', { choice: 'stay', step: step ?? null });
            return;
        }
        if (imoSheetOpen) {
            setImoSheetOpen(false);
            return;
        }
        if (birthSheetOpen) {
            // 생일 시트에서 뒤로 = 다음에요(14 D1, 15 X5)
            setBirthSheetOpen(false);
            counter.send({ chipId: 'decline' });
            return;
        }
        track('counter_exit_tap', { step: step ?? null });
        if ((!step || step === 'tea') && !saidAnything) {
            leave();
            return;
        }
        setExitOpen(true);
    }
    const requestExitRef = useRef(requestExit);
    requestExitRef.current = requestExit;

    // 브라우저 뒤로(iOS 가장자리 스와이프 포함): 기록을 하나 더 쌓아 두고, 뒤로가 오면 나가기 동작으로 바꿈
    useEffect(() => {
        window.history.pushState({ counterGuard: true }, '', window.location.href);
        const onPop = () => {
            window.history.pushState({ counterGuard: true }, '', window.location.href);
            requestExitRef.current();
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // 장부 카드를 다시 보인 경우(다시 온 인사) 버튼은 마지막 카드에만
    const lastLedgerId = messages.filter((message) => message.scene?.type === 'ledger').at(-1)?.messagesId ?? null;

    return (
        <div
            className={`flex h-dvh flex-col transition-opacity duration-[240ms] ${leaving ? 'opacity-0' : 'opacity-100'}`}
        >
            <header className="flex h-14 shrink-0 items-center gap-1.5 border-b border-hairline pr-4 pl-1">
                <button
                    type="button"
                    aria-label={EXIT_LABEL}
                    data-nav-back=""
                    onClick={requestExit}
                    className="flex size-11 shrink-0 items-center justify-center text-text-primary"
                >
                    <ChevronLeft size={22} strokeWidth={2} aria-hidden="true" />
                </button>
                <Avatar memberKey="imo" size={32} />
                <div className="flex flex-col">
                    <h1 className="m-0 text-[15px] leading-5 font-semibold">{IMO.name}</h1>
                    <span className="text-micro font-normal text-text-secondary">인생상회 카운터</span>
                </div>
            </header>

            <main ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
                <span className="self-center text-micro font-normal tracking-[0.06em] text-label-muted">
                    {partOfDay()} · 처음 온 날
                </span>
                {counter.status === 'error' ? (
                    <div className="flex flex-col items-center gap-3 py-10 text-center">
                        <p role="alert" className="m-0 text-caption text-error">
                            {counter.error}
                        </p>
                        <Button variant="secondary" size="md" onClick={counter.reload}>
                            다시 불러오기
                        </Button>
                    </div>
                ) : null}

                {messages.map((message, index) =>
                    message.scene?.type === 'ledger' && view?.ledger ? (
                        <LedgerCard
                            key={message.messagesId}
                            ledger={view.ledger}
                            step={showControls && message.messagesId === lastLedgerId ? step : null}
                            busy={counter.sending}
                            onAccept={() => counter.send({ chipId: 'accept' })}
                            onDecline={() => counter.send({ chipId: 'decline' })}
                        />
                    ) : (
                        <CounterMessage
                            key={message.messagesId}
                            message={message}
                            continued={messages[index - 1]?.role === 'member'}
                            onOpenImo={() => setImoSheetOpen(true)}
                        />
                    )
                )}
                {showControls && step === 'edit' ? (
                    <SummaryCard
                        draft={view.summaryDraft}
                        busy={counter.sending}
                        onPost={(text) => counter.send({ post: text })}
                        onCancel={() => counter.send({ chipId: 'cancelPost' })}
                    />
                ) : null}

                {/* 내 말이 먼저 붙고 그 아래 이모 입력 중 */}
                {counter.pendingSpeech ? <MyMessage body={counter.pendingSpeech} status="sending" /> : null}
                {counter.imoTyping ? <TypingIndicator memberKey="imo" name={IMO.name} /> : null}
            </main>

            <footer className="flex shrink-0 flex-col gap-2.5 border-t border-hairline bg-bg px-4 pt-2.5 pb-[max(14px,env(safe-area-inset-bottom))]">
                {counter.error && counter.status === 'ready' ? (
                    <div className="flex items-center justify-between gap-2 text-caption">
                        <span role="alert" className="text-error">
                            {counter.error}
                        </span>
                        {counter.canRetry ? (
                            <button
                                type="button"
                                onClick={counter.retry}
                                className="min-h-touch px-1 font-semibold text-brand"
                            >
                                다시 보내기
                            </button>
                        ) : null}
                    </div>
                ) : null}
                {showControls ? <Controls view={view} sending={counter.sending} onSend={counter.send} /> : null}
                {!showControls || !hasControls(view) ? <span className="h-11" aria-hidden="true" /> : null}
            </footer>

            {showControls && step === 'card' && view.regular ? <RegularCard regular={view.regular} /> : null}
            <ImoSheet open={imoSheetOpen} onClose={() => setImoSheetOpen(false)} />
            <ExitSheet
                open={exitOpen}
                speech={view?.speech ?? 'casual'}
                line={view?.lines?.exitHold ?? null}
                onStay={() => {
                    setExitOpen(false);
                    track('counter_exit_hold', { choice: 'stay', step: step ?? null });
                }}
                onLeave={() => {
                    track('counter_exit_hold', { choice: 'leave', step: step ?? null });
                    leave();
                }}
            />
            <LedgerBirthSheet
                open={birthSheetOpen}
                busy={counter.sending}
                onSubmit={(birth) => {
                    setBirthSheetOpen(false);
                    counter.send({ birth });
                }}
                onDecline={() => {
                    setBirthSheetOpen(false);
                    counter.send({ chipId: 'decline' });
                }}
            />
        </div>
    );
}

/** 이모 명함 카드를 누르면: 첫 방문 중에는 프로필 화면 대신 시트로 (단골 등록 전이라 다른 화면을 열지 않습니다) */
function ImoSheet({ open, onClose }) {
    return (
        <BottomSheet open={open} onClose={onClose} label={`${IMO.name} 소개`}>
            <div className="flex items-center gap-3">
                <Avatar memberKey="imo" size={64} />
                <div className="flex flex-col gap-0.5">
                    <h2 className="m-0 text-[18px] font-bold">{IMO.name}</h2>
                    <span className="text-label font-normal text-text-secondary">
                        {IMO.role} · {IMO.line}
                    </span>
                </div>
            </div>
            <p className="m-0 rounded-[12px] bg-bg px-3.5 py-3 font-serif text-[16px] leading-[26px]">{IMO.quote}</p>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {IMO.story.map((line) => (
                    <li key={line} className="text-[14px] leading-[22px] text-text-secondary">
                        {line}
                    </li>
                ))}
            </ul>
            <p className="m-0 text-micro font-normal text-label-muted">순례 이모는 AI가 연기하는 가상 인물이에요.</p>
        </BottomSheet>
    );
}

function hasControls(view) {
    if (!view) {
        return false;
    }
    return view.controls.chips.length > 0 || Boolean(view.controls.input);
}

/** continued: 바로 앞도 이모 말이면 초상 생략(14 L6) */
function CounterMessage({ message, continued = false, onOpenImo }) {
    if (message.role === 'user') {
        return <MyMessage body={message.body} />;
    }
    if (message.role === 'member') {
        return (
            <MemberMessage
                memberKey="imo"
                name={IMO.name}
                showName={false}
                showAvatar={!continued}
                body={message.body}
            />
        );
    }
    if (message.scene?.type === 'safety') {
        // 예전에 남은 안전 카드 줄도 카드 없이 전화 버튼으로(C-020)
        return <SceneLine scene={{ type: 'call_links', level: message.safetyLevel }} />;
    }
    return message.scene ? <SceneLine scene={message.scene} onOpenProfile={onOpenImo} /> : null;
}

/** 칩 줄과 한 줄 입력 */
function Controls({ view, sending, onSend }) {
    const [draft, setDraft] = useState('');
    const { input } = view.controls;
    // 장부의 버튼은 장부 카드 안에 있습니다(08 K4)
    const chips = view.step === 'offer' ? [] : view.controls.chips;
    const text = draft.trim();

    function submitText() {
        if (!text || sending) {
            return;
        }
        onSend({ body: text }).then((response) => {
            if (response) {
                setDraft('');
            }
        });
    }

    return (
        <>
            {chips.length ? (
                <div className="flex flex-wrap gap-2">
                    {chips.map((chip) => (
                        <button
                            key={chip.id}
                            type="button"
                            disabled={sending}
                            onClick={() => onSend({ chipId: chip.id })}
                            className={`h-10 rounded-[20px] border bg-surface px-4 text-[15px] disabled:opacity-40 ${
                                chip.id === 'worryCard' ? 'border-accent text-accent-text' : 'border-chip-line'
                            }`}
                        >
                            {chip.label}
                        </button>
                    ))}
                </div>
            ) : null}
            {input ? (
                <form
                    className="flex items-center gap-2"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submitText();
                    }}
                >
                    <InputBox className="flex h-11 min-w-0 flex-1 rounded-card border border-hairline bg-surface">
                        <input
                            type="text"
                            aria-label={input.placeholder}
                            placeholder={input.placeholder}
                            maxLength={input.maxLength}
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            className="h-full min-w-0 flex-1 rounded-card border-0 bg-transparent px-3.5 text-body placeholder:text-text-tertiary"
                        />
                    </InputBox>
                    <button
                        type="submit"
                        aria-label="보내기"
                        disabled={!text || sending}
                        className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand disabled:opacity-40"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M12 19V5" />
                            <path d="M6 11l6-6 6 6" />
                        </svg>
                    </button>
                </form>
            ) : null}
        </>
    );
}

/**
 * 「별채에 올릴 말」 (프로토타입 isSummaryOpen). 초안은 유저가 한 말로만 만든 것이고,
 * 유저가 고친 문장이 그대로 걸립니다.
 */
function SummaryCard({ draft, busy, onPost, onCancel }) {
    const [text, setText] = useState(draft ?? '');
    const [loading, setLoading] = useState(draft === null || draft === undefined);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (draft !== null && draft !== undefined) {
            return undefined;
        }
        let active = true;
        counterApi
            .draftCounterSummary()
            .then((response) => {
                if (active) {
                    setText(response.draft);
                }
            })
            .catch((requestError) => {
                if (active) {
                    setError(errorMessageOf(requestError, '초안을 못 만들었어요. 직접 적어 주세요.'));
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [draft]);

    const trimmed = text.trim();

    return (
        <section
            aria-label="별채에 올릴 말"
            className="flex flex-col gap-2.5 rounded-card border border-hairline bg-surface p-4"
        >
            <span className="text-section text-label-muted">별채에 올릴 말</span>
            <textarea
                aria-label="별채에 올릴 말"
                rows={3}
                maxLength={SUMMARY_MAX_LENGTH}
                value={text}
                disabled={loading}
                placeholder={loading ? '이모가 받아 적는 중이에요' : ''}
                onChange={(event) => setText(event.target.value)}
                className="w-full resize-none rounded-[12px] border border-hairline bg-bg px-3 py-2.5 text-body"
            />
            {error ? <span className="text-caption text-error">{error}</span> : null}
            <span className="text-caption leading-5 text-text-secondary">
                이 말만 별채에 내 첫 말로 올라가요. 이모랑 나눈 대화는 올라가지 않아요.
            </span>
            <div className="flex gap-2">
                <Button
                    variant="text"
                    size="md"
                    className="px-3.5 text-text-secondary"
                    disabled={busy}
                    onClick={onCancel}
                >
                    인사만 할게요
                </Button>
                <Button
                    size="md"
                    className="flex-1"
                    busy={busy}
                    disabled={!trimmed || loading}
                    onClick={() => onPost(text)}
                >
                    이대로 올리기
                </Button>
            </div>
        </section>
    );
}
